/* eslint-disable react/display-name */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { FasSave, FasTimes } from 'assets/icons';
import { useSelector } from 'react-redux';
import { ATCONSTANTS as ATC } from 'appconstants';
import { getAttributesArraySelector } from 'reducers/packets';
import PropTypes from 'prop-types';
import Select from 'react-select';
import InstitutionTypeAhead from 'components/Fields/typeAheads/InstitutionTypeAhead';
import ProtocolTypeAhead from 'components/Fields/typeAheads/ProtocolTypeAhead';
import PersonTypeAhead from 'components/Fields/typeAheads/PersonTypeAhead';
import PatientTypeAhead from 'components/Fields/typeAheads/PatientTypeAhead';
import useScrollOnNewRow from 'components/grid/useScrollOnNewRow';

const EMPTY_SELECT = { label: '', value: '' };
const SCROLL_ELEMENT_KEY = 'INDEXING_ADD_NEW_RECORD';

const IndexingEditRow = forwardRef(({
  row, columns, cancelEdit, reload, onClickSaveIcon, packet
}, ref) => {
  const { packetId, object } = packet;
  const attributesOptionsArray = useSelector(getAttributesArraySelector);

  useScrollOnNewRow(SCROLL_ELEMENT_KEY, Object.keys(row).length === 0);

  const initCurrentCtepSelectObject = () => {
    let initialObj = EMPTY_SELECT;
    columns.forEach((column) => {
      const { name } = column;
      if (
        (name === 'siteCtepId' && row.siteCtepId)
        || (name === 'personCtepId' && row.personCtepId)
        || name === 'patientId'
      ) {
        initialObj = { label: row[name], value: row[name] };
        if (name === 'siteCtepId') {
          initialObj = {
            ...initialObj,
            siteName: row.siteName
          };
        } else if (name === 'personCtepId') {
          initialObj = {
            ...initialObj,
            personName: row.personName
          };
        } else if (name === 'patientId') {
          initialObj = {
            ...initialObj,
            protocolNumber: row.protocolNumber
          };
        }
      }
      return EMPTY_SELECT;
    });
    return initialObj;
  };

  const initProtocolNumberObject = () => {
    let initialObj = EMPTY_SELECT;
    columns.forEach((column) => {
      const { name } = column;
      if ((name === 'protocolNumber' && row.protocolNumber) || name === 'objKeyValue') {
        initialObj = { label: row[name], value: row[name] };
      }
    });
    return initialObj;
  };

  const [rowToEditObject, setRowToEditObject] = useState(row);
  const [currentCtepSelectObject, setCurrentCtepSelectObject] = useState(initCurrentCtepSelectObject());
  const [currentProtocolSelectObject, setCurrentProtocolSelectObject] = useState(initProtocolNumberObject());

  const onSave = (isProcessButtonClicked) => {
    let processUpdatedRowForSaveObject = { ...rowToEditObject };
    if (!processUpdatedRowForSaveObject.dmsPktMapId) {
      const reqParams = {
        pktId: packetId,
        objType: object
      };
      processUpdatedRowForSaveObject = {
        ...processUpdatedRowForSaveObject,
        ...reqParams
      };
    }

    onClickSaveIcon(processUpdatedRowForSaveObject, isProcessButtonClicked);
  };

  useImperativeHandle(ref, () => ({
    triggerSave() {
      onSave(true);
    }
  }));

  const onCancel = () => {
    cancelEdit();
    // User cancelled a copied row
    if (row.dmsPktMapId == null) {
      reload();
    }
  };

  const getCell = (column) => {
    const { isAction, name, renderCell } = column;
    if (isAction) {
      return ' ';
    }
    return renderCell != null ? renderCell(row) : row[name];
  };

  const dropDownOnChange = (selectedValue, name, isCetpInput) => {
    const setState = isCetpInput ? setCurrentCtepSelectObject : setCurrentProtocolSelectObject;
    setState(selectedValue === null ? EMPTY_SELECT : selectedValue);
    let updateSiteCtep = {
      ...rowToEditObject,
      [name]: selectedValue ? selectedValue.value : null
    };
    if (name === 'siteCtepId' || name === 'personCtepId' || name === 'patientId') {
      const extraData = { ...selectedValue };
      if (selectedValue !== null) {
        delete extraData.label;
        delete extraData.value;
      }
      updateSiteCtep = {
        ...updateSiteCtep,
        ...extraData
      };
    }
    setRowToEditObject(updateSiteCtep);
  };

  const renderEditRow = (_, name) => {
    const isCetpInput = name === 'siteCtepId' || name === 'personCtepId' || name === 'patientId';
    const currentValue = isCetpInput ? currentCtepSelectObject : currentProtocolSelectObject;
    let SelectComponent = null;

    switch (name) {
      case 'siteCtepId':
        SelectComponent = InstitutionTypeAhead;
        break;
      case 'protocolNumber':
        SelectComponent = ProtocolTypeAhead;
        break;
      case 'objKeyValue':
        SelectComponent = Select;
        break;
      case 'personCtepId':
        SelectComponent = PersonTypeAhead;
        break;
      case 'patientId':
        SelectComponent = PatientTypeAhead;
        break;
      default:
        break;
    }

    if (SelectComponent === null) {
      return null;
    }

    const extraProps = {};
    if (name === 'objKeyValue') {
      extraProps.options = attributesOptionsArray;
      extraProps.menuPortalTarget = document.querySelector('body');
      extraProps.className = 'reactSelect-wrapper';
      extraProps.classNamePrefix = 'reactSelect';
    }

    return (
      <SelectComponent
        onChange={(selectedValue) => dropDownOnChange(selectedValue, name, isCetpInput)}
        value={currentValue}
        {...extraProps}
      />
    );
  };

  const renderRows = (column) => {
    const { isEditable, name } = column;
    if (isEditable) {
      return renderEditRow(isEditable, name);
    }
    if (name === 'siteName' || name === 'personName') {
      const selectedName = name === 'siteName' ? currentCtepSelectObject.siteName : currentCtepSelectObject.personName;
      return <span>{selectedName}</span>;
    }
    if (object === 'ENROLLMENT_TRANSFERS' && name === 'protocolNumber') {
      return <span>{currentCtepSelectObject.protocolNumber}</span>;
    }
    return getCell(column);
  };

  return (
    <tr id={SCROLL_ELEMENT_KEY}>
      {columns.map((column) => (
        <td key={column.name}>{renderRows(column)}</td>
      ))}
      <td>
        <span className="action-icon-container">
          <button
            data-id={`${ATC.PACKETID}${row.id}_SAVE${ATC.BUTTON}`}
            type="button"
            onClick={() => onSave()}
            className="fn-btn font-lg mr-1"
          >
            <FasSave />
          </button>
          <button
            data-id={`${ATC.PACKETID}${row.id}_CANCEL${ATC.BUTTON}`}
            type="button"
            onClick={onCancel}
            className="fn-btn font-lg mr-1"
          >
            <FasTimes />
          </button>
        </span>
      </td>
    </tr>
  );
});

const propTypes = {
  // row: PropTypes.oneOf(PropTypes.array).isRequired,
  // columns: PropTypes.oneOf(PropTypes.array).isRequired,
  cancelEdit: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  onClickSaveIcon: PropTypes.func.isRequired
  // context: PropTypes.oneOf(PropTypes.object).isRequired,
  // packet: PropTypes.oneOf(PropTypes.object).isRequired
};

IndexingEditRow.propTypes = propTypes;
export default IndexingEditRow;
